/**
 * CardActivitySection — the CardDetailDrawer's Activity (audit trail) block
 * with its "show more" toggle + per-row revert, plus the sensitivity banner.
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 */
import type { KanbanApi } from '../../../api'
import type { AuditLogEntry, Card } from '../../../types'
import { _humaniseAuditEvent, ActivityRow } from '../activity'
import type { DrawerData, DrawerEditing } from '../drawerHookTypes'

export interface CardActivitySectionProps {
  card: Card
  api: KanbanApi
  allLog: AuditLogEntry[]
  editing: DrawerEditing
  data: DrawerData
  /** FEAT-2026-06-30-KANBAN-MERGE-UX — open another card in the drawer so
   *  merge audit rows can deep-link to the keeper / archived loser. May
   *  return a promise so the row can degrade on a failed fetch. */
  onOpenCardId?: (cardId: string) => void | Promise<void>
  /** ARC-4: suppress the inner h3 when a CollapsibleSection hosts this. */
  hideTitle?: boolean
}

export function CardActivitySection({ card, api, allLog, editing, data, onOpenCardId, hideTitle = false }: CardActivitySectionProps) {
  const { logExpanded, setLogExpanded } = editing
  const { laneNameById, resolveActorName, pushFocusedCardUpdate } = data

  return (
    <>
      <section>
        <div className={`mb-1 flex items-center ${hideTitle ? 'justify-end' : 'justify-between'}`}>
          {/* ARC-4: hideTitle when hosted inside a CollapsibleSection (which
              already renders the "Activity" header + count). */}
          {!hideTitle && (
            <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
              Activity
              {allLog.length > 0 && (
                <span className="ml-1 font-normal text-neutral-400">({allLog.length})</span>
              )}
            </h3>
          )}
          {allLog.length > 8 && (
            <button
              type="button"
              onClick={() => setLogExpanded((e) => !e)}
              className="text-[10.5px] font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              {logExpanded ? 'Show recent' : `Show all (${allLog.length})`}
            </button>
          )}
        </div>
        {allLog.length === 0 ? (
          <p className="text-xs text-neutral-400">No activity yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {(logExpanded ? allLog : allLog.slice(0, 8)).map((entry, i) => {
              const fromName =
                entry.event === 'lane_changed' && entry.from_lane
                  ? laneNameById.get(String(entry.from_lane)) ?? String(entry.from_lane).slice(0, 8)
                  : null
              const toName =
                entry.event === 'lane_changed' && entry.to_lane
                  ? laneNameById.get(String(entry.to_lane)) ?? String(entry.to_lane).slice(0, 8)
                  : null
              const niceEvent = _humaniseAuditEvent(entry)
              return (
                <ActivityRow
                  key={i}
                  entry={entry}
                  niceEvent={niceEvent}
                  fromName={fromName}
                  toName={toName}
                  actorName={resolveActorName(entry.by)}
                  card={card}
                  api={api}
                  laneNameById={laneNameById}
                  onReverted={(updated) => pushFocusedCardUpdate(updated)}
                  onOpenCardId={onOpenCardId}
                />
              )
            })}
          </ul>
        )}
      </section>

      {/* Sensitivity warning */}
      {card.sensitive && (
        <section className="rounded-md border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          <strong>Sensitive:</strong> {card.sensitivity_reason || 'flagged by classifier'}
        </section>
      )}
    </>
  )
}
